import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Grid
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import customYoutubeApi from '../api/customYoutubeApi';

const CustomYoutubeQRViewer = () => {
  const { customVideoId } = useParams();
  const navigate = useNavigate();
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (customVideoId) {
      loadQRCode();
    }
  }, [customVideoId]);

  const loadQRCode = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await customYoutubeApi.getQRCode(customVideoId);
      setQrData(response.data);
    } catch (err) {
      console.error('Error loading QR code:', err);
      if (err.response?.status === 404) {
        setError('Video not found. It may have been removed or is no longer available.');
      } else {
        setError(`Failed to load QR code: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (qrData?.qrCodeDataUrl) {
      const link = document.createElement('a');
      link.download = `qr-${qrData.customVideoId}.png`;
      link.href = qrData.qrCodeDataUrl;
      link.click();
    }
  };

  const handleShare = async () => {
    const url = qrData?.videoUrl || window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: qrData?.videoTitle || 'Custom YouTube Video',
          text: `Check out this custom video: ${qrData?.videoTitle || ''}`,
          url: url
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Link copied to clipboard!');
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '50vh',
        p: 3 
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Generating QR Code...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/youtube-video/${customVideoId}`)}
          sx={{ mb: 2 }}
        >
          Back to Video
        </Button>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            onClick={loadQRCode}
          >
            Try Again
          </Button>
          <Button 
            variant="contained" 
            onClick={() => navigate('/custom-youtube-videos')}
          >
            Browse Videos
          </Button>
        </Box>
      </Box>
    );
  }

  if (!qrData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5">QR Code not available</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate(`/youtube-video/${customVideoId}`)}
          sx={{ mt: 2 }}
        >
          Back to Video
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/youtube-video/${customVideoId}`)}
          sx={{ mr: 2 }}
        >
          Back to Video
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          📱 QR Code
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* QR Code */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={3}
            sx={{ 
              p: 3, 
              textAlign: 'center',
              backgroundColor: '#fff'
            }}
          >
            <img 
              src={qrData.qrCodeDataUrl} 
              alt="QR Code" 
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                borderRadius: '8px'
              }}
            />
            
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              {qrData.videoTitle}
            </Typography>
            
            <Typography variant="caption" color="text.secondary" display="block">
              Video ID: {qrData.customVideoId}
            </Typography>
          </Paper>
        </Grid>

        {/* Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📋 QR Code Information
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Video Title:
                </Typography>
                <Typography variant="body1">
                  {qrData.videoTitle}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Direct Link:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    wordBreak: 'break-all',
                    backgroundColor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                    fontFamily: 'monospace'
                  }}
                >
                  {qrData.videoUrl}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Video ID:
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {qrData.customVideoId}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                >
                  Download QR Code
                </Button>
                
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                >
                  Share Video Link
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ViewIcon />}
                  onClick={() => navigate(`/youtube-video/${customVideoId}`)}
                >
                  View Video
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Usage Instructions */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📱 How to Use
              </Typography>
              
              <Typography variant="body2" component="div" sx={{ lineHeight: 1.6 }}>
                <strong>1. Scan the QR Code</strong><br />
                Use any QR code scanner app on your phone or tablet
                <br /><br />
                
                <strong>2. Open the Link</strong><br />
                The QR code will open the video directly in your browser
                <br /><br />
                
                <strong>3. Share & Print</strong><br />
                You can download and print this QR code for physical distribution
                <br /><br />
                
                <strong>4. Works Offline</strong><br />
                Once downloaded, the QR code works independently and doesn't need this page
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomYoutubeQRViewer;